import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface AccessibilitySettings {
  reducedMotion: boolean;
  highContrast: boolean;
  largeText: boolean;
  focusVisible: boolean;
  screenReader: boolean;
  keyboardNavigation: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  colorScheme: 'auto' | 'light' | 'dark' | 'high-contrast';
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSetting: <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => void;
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  isKeyboardUser: boolean;
  focusRing: boolean;
  skipToContent: () => void;
  resetSettings: () => void;
}

const defaultSettings: AccessibilitySettings = {
  reducedMotion: false,
  highContrast: false,
  largeText: false,
  focusVisible: true,
  screenReader: false,
  keyboardNavigation: false,
  fontSize: 'medium',
  colorScheme: 'auto',
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

interface Props {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<Props> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [isKeyboardUser, setIsKeyboardUser] = useState(false);
  const [focusRing, setFocusRing] = useState(false);
  const ariaLiveRef = useRef<HTMLDivElement>(null);

  // Load settings from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('accessibility-settings');
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        setSettings(prevSettings => ({ ...prevSettings, ...parsedSettings }));
      }
    } catch (error) {
      console.warn('Failed to load accessibility settings:', error);
    }

    // Detect system preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    setSettings(prev => ({
      ...prev,
      reducedMotion: prefersReducedMotion || prev.reducedMotion,
      highContrast: prefersHighContrast || prev.highContrast,
      colorScheme:
        prev.colorScheme === 'auto' ? (prefersDark ? 'dark' : 'light') : prev.colorScheme,
    }));
  }, []);

  // Detect keyboard usage
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setIsKeyboardUser(true);
        setFocusRing(true);
      }
    };

    const handleMouseDown = () => {
      setIsKeyboardUser(false);
      setFocusRing(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Remove existing classes
    body.classList.remove(
      'reduced-motion',
      'high-contrast',
      'large-text',
      'keyboard-navigation',
      'font-small',
      'font-medium',
      'font-large',
      'font-xlarge'
    );

    // Apply settings
    if (settings.reducedMotion) {
      body.classList.add('reduced-motion');
    }

    if (settings.highContrast) {
      body.classList.add('high-contrast');
    }

    if (settings.largeText) {
      body.classList.add('large-text');
    }

    if (isKeyboardUser || settings.keyboardNavigation) {
      body.classList.add('keyboard-navigation');
    }

    // Apply font size
    body.classList.add(`font-${settings.fontSize}`);

    // Apply color scheme
    root.setAttribute('data-color-scheme', settings.colorScheme);

    // Set CSS custom properties
    root.style.setProperty('--focus-visible', focusRing ? '1' : '0');

    // Save settings to localStorage
    try {
      localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save accessibility settings:', error);
    }
  }, [settings, isKeyboardUser, focusRing]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (ariaLiveRef.current) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', priority);
      announcement.className = 'sr-only';
      announcement.textContent = message;

      ariaLiveRef.current.appendChild(announcement);

      // Remove after announcement
      setTimeout(() => {
        if (ariaLiveRef.current && announcement.parentNode) {
          ariaLiveRef.current.removeChild(announcement);
        }
      }, 1000);
    }
  };

  const skipToContent = () => {
    const main = document.querySelector('main') || document.querySelector('#main-content');
    if (main) {
      (main as HTMLElement).focus();
      announceToScreenReader('Skipped to main content');
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem('accessibility-settings');
    announceToScreenReader('Accessibility settings reset to default');
  };

  const contextValue: AccessibilityContextType = {
    settings,
    updateSetting,
    announceToScreenReader,
    isKeyboardUser,
    focusRing,
    skipToContent,
    resetSettings,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="skip-link"
        onClick={e => {
          e.preventDefault();
          skipToContent();
        }}
      >
        Skip to main content
      </a>

      {/* Screen reader announcements */}
      <div ref={ariaLiveRef} aria-live="polite" aria-atomic="false" className="sr-only" />

      {children}
    </AccessibilityContext.Provider>
  );
};

// Accessibility toolbar component
export const AccessibilityToolbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { settings, updateSetting, announceToScreenReader, resetSettings } = useAccessibility();

  const handleToggle = () => {
    setIsOpen(!isOpen);
    announceToScreenReader(isOpen ? 'Accessibility menu closed' : 'Accessibility menu opened');
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      <button
        onClick={handleToggle}
        className="p-3 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        aria-label="Open accessibility options"
        aria-expanded={isOpen}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="absolute top-full right-0 mt-2 w-80 bg-popover text-popover-foreground rounded-lg shadow-xl border border-border p-6"
            role="dialog"
            aria-label="Accessibility settings"
          >
            <h2 className="text-lg font-semibold mb-4">Accessibility Options</h2>

            <div className="space-y-4">
              {/* Reduced Motion */}
              <label className="flex items-center justify-between">
                <span className="text-sm font-medium">Reduce animations</span>
                <input
                  type="checkbox"
                  checked={settings.reducedMotion}
                  onChange={e => updateSetting('reducedMotion', e.target.checked)}
                  className="rounded"
                />
              </label>

              {/* High Contrast */}
              <label className="flex items-center justify-between">
                <span className="text-sm font-medium">High contrast</span>
                <input
                  type="checkbox"
                  checked={settings.highContrast}
                  onChange={e => updateSetting('highContrast', e.target.checked)}
                  className="rounded"
                />
              </label>

              {/* Large Text */}
              <label className="flex items-center justify-between">
                <span className="text-sm font-medium">Large text</span>
                <input
                  type="checkbox"
                  checked={settings.largeText}
                  onChange={e => updateSetting('largeText', e.target.checked)}
                  className="rounded"
                />
              </label>

              {/* Font Size */}
              <div>
                <label htmlFor="font-size" className="block text-sm font-medium mb-2">
                  Font size
                </label>
                <select
                  id="font-size"
                  value={settings.fontSize}
                  onChange={e => updateSetting('fontSize', e.target.value as any)}
                  className="w-full p-2 bg-background text-foreground border border-border rounded-md"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="xlarge">Extra Large</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-border">
                <button
                  onClick={resetSettings}
                  className="flex-1 px-3 py-2 text-sm border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  Reset
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-3 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Hook for accessible focus management
export const useFocusManagement = () => {
  const { isKeyboardUser } = useAccessibility();

  const focusElement = (selector: string) => {
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      element.focus();
    }
  };

  const trapFocus = (containerRef: React.RefObject<HTMLElement>) => {
    useEffect(() => {
      const container = containerRef.current;
      if (!container || !isKeyboardUser) return;

      const focusableElements = container.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      );

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };

      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }, [isKeyboardUser]);
  };

  return { focusElement, trapFocus };
};

// Component for accessible live regions
interface LiveRegionProps {
  children: React.ReactNode;
  priority?: 'polite' | 'assertive';
  atomic?: boolean;
}

export const LiveRegion: React.FC<LiveRegionProps> = ({
  children,
  priority = 'polite',
  atomic = true,
}) => (
  <div aria-live={priority} aria-atomic={atomic} className="sr-only">
    {children}
  </div>
);

export default AccessibilityProvider;
